// AdminAnalyticsPage — Phase 3 Dashboard Analytics
// Aggregates per-cycle KPIs from Stripe, Resend, Newsletter signups, GA4,
// Search Console and ContentStudio. The AI orchestrator reads the stored
// `measurements` field on each cycle to learn which themes performed.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3, Calendar, Mail, ShoppingCart, Users, Globe, Search,
  Megaphone, Loader2, AlertTriangle, CheckCircle2, Clock, Zap, RefreshCw,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const SOURCE_META = {
  stripe:         { label: "Boutique (Stripe)",      icon: ShoppingCart, color: "#6b2bd9" },
  resend:         { label: "Emails (Resend)",         icon: Mail,         color: "#e8a95b" },
  newsletter:     { label: "Newsletter",              icon: Users,        color: "#8b1538" },
  inbound:        { label: "Signaux entrants",        icon: Megaphone,    color: "#2563eb" },
  ga4:            { label: "Trafic (GA4)",            icon: Globe,        color: "#0f766e" },
  search_console: { label: "Recherche (SC)",          icon: Search,       color: "#059669" },
  contentstudio:  { label: "Social (ContentStudio)",  icon: BarChart3,    color: "#be185d" },
};

const STATUS_PILL = {
  ok:             { bg: "bg-emerald-50", fg: "text-emerald-700", icon: CheckCircle2, label: "Connecté" },
  partial:        { bg: "bg-amber-50",   fg: "text-amber-700",   icon: Clock,         label: "Partiel"   },
  not_configured: { bg: "bg-stone-50",   fg: "text-stone-500",   icon: AlertTriangle, label: "À configurer" },
  error:          { bg: "bg-rose-50",    fg: "text-rose-700",    icon: AlertTriangle, label: "Erreur" },
};

const StatusPill = ({ status }) => {
  const m = STATUS_PILL[status] || STATUS_PILL.error;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${m.bg} ${m.fg}`}>
      <Icon size={10} /> {m.label}
    </span>
  );
};

const MetricCard = ({ srcKey, data }) => {
  const meta = SOURCE_META[srcKey] || {};
  const Icon = meta.icon || BarChart3;
  const keys = Object.keys(data || {}).filter((k) => !["status", "reason"].includes(k));
  return (
    <div className="bg-white rounded-[6px] border border-[color:var(--gv-line)] p-4" data-testid={`analytics-card-${srcKey}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: meta.color + "20", color: meta.color }}>
            <Icon size={14} />
          </div>
          <div className="font-display text-[14px] font-semibold">{meta.label || srcKey}</div>
        </div>
        <StatusPill status={data?.status} />
      </div>
      {data?.reason && (
        <div className="text-[11px] text-stone-500 mb-3 italic">{data.reason}</div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {keys.map((k) => (
          <div key={k} className="bg-stone-50 rounded-[4px] p-2">
            <div className="text-[10px] uppercase tracking-wide text-stone-500">{k.replaceAll("_", " ")}</div>
            <div className="text-[18px] font-semibold text-[color:var(--gv-ink)]">
              {typeof data[k] === "number" ? data[k].toLocaleString("fr-FR") : String(data[k])}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminAnalyticsPage = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [period, setPeriod] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, c, cfg] = await Promise.all([
        adminApi.analyticsPeriod(year, month),
        adminApi.analyticsCycles(24),
        adminApi.analyticsConfigStatus(),
      ]);
      setPeriod(p);
      setCycles(c?.cycles || []);
      setConfig(cfg);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await adminApi.monthlyCycleTriggerAuto();
      // Give DeepSeek ~20s to generate before refetching
      setTimeout(load, 22000);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setTimeout(() => setTriggering(false), 22000);
    }
  };

  const monthLabel = useMemo(() => {
    const d = new Date(year, month - 1, 1);
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [year, month]);

  const years = useMemo(() => {
    const arr = [];
    for (let y = now.getFullYear(); y >= 2024; y--) arr.push(y);
    return arr;
  }, [now]);
  const months = [
    [1, "Janvier"], [2, "Février"], [3, "Mars"], [4, "Avril"],
    [5, "Mai"], [6, "Juin"], [7, "Juillet"], [8, "Août"],
    [9, "Septembre"], [10, "Octobre"], [11, "Novembre"], [12, "Décembre"],
  ];

  return (
    <div className="min-h-screen" data-testid="admin-analytics">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">Dashboard Analytics</h1>
          <p className="text-sm text-stone-500 mt-1">
            KPIs par cycle pour mesurer l'impact de chaque thème du mois et nourrir l'apprentissage de l'IA.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleTrigger}
            disabled={triggering}
            data-testid="trigger-monthly-cron-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] bg-[color:var(--gv-ink)] text-white text-[13px] disabled:opacity-50"
            title="Lance manuellement le cron du 1er lundi (DeepSeek + email Audrey)"
          >
            {triggering ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
            Tester cron mensuel
          </button>
          <button
            onClick={load}
            disabled={loading}
            data-testid="analytics-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-[color:var(--gv-line)] bg-white text-[13px] disabled:opacity-50"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Rafraîchir
          </button>
        </div>
      </header>

      {/* Config status banner */}
      {config && (
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-3 mb-4 flex flex-wrap items-center gap-3 text-[12px]" data-testid="config-status">
          <div className="font-semibold text-stone-600">Connecteurs :</div>
          {Object.entries(config).map(([k, v]) => {
            const configured = Object.values(v).every(Boolean);
            return (
              <span key={k} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${configured ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                {configured ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />} {k}
              </span>
            );
          })}
        </div>
      )}

      {/* Period selector */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-4 mb-4 flex items-center gap-3">
        <Calendar size={14} className="text-stone-500" />
        <span className="text-[13px] font-medium">Période :</span>
        <select
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          className="px-2 py-1.5 rounded-[4px] border border-[color:var(--gv-line)] text-[13px]"
          data-testid="analytics-month-select"
        >
          {months.map(([n, label]) => <option key={n} value={n}>{label}</option>)}
        </select>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="px-2 py-1.5 rounded-[4px] border border-[color:var(--gv-line)] text-[13px]"
          data-testid="analytics-year-select"
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-[12px] text-stone-500 ml-auto capitalize">{monthLabel}</span>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-3 text-sm text-rose-700 mb-4" data-testid="analytics-error">
          {error}
        </div>
      )}

      {/* Period KPI grid */}
      {period && (
        <section className="mb-6">
          <h2 className="font-display text-[18px] font-semibold mb-3">KPIs du mois · {monthLabel}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {["stripe", "newsletter", "resend", "inbound", "ga4", "search_console", "contentstudio"].map((k) => (
              <MetricCard key={k} srcKey={k} data={period[k]} />
            ))}
          </div>
        </section>
      )}

      {/* Cycle history */}
      <section>
        <h2 className="font-display text-[18px] font-semibold mb-3">Historique des cycles ({cycles.length})</h2>
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-stone-50 text-stone-600 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Cycle</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Thème</th>
                <th className="text-left px-3 py-2">Région</th>
                <th className="text-left px-3 py-2">Statut</th>
                <th className="text-right px-3 py-2">Revenu</th>
                <th className="text-right px-3 py-2">Signups</th>
                <th className="text-right px-3 py-2">Emails</th>
                <th className="text-right px-3 py-2">Signaux</th>
              </tr>
            </thead>
            <tbody>
              {cycles.map((c) => {
                const m = c.measurements || {};
                return (
                  <tr key={c.cycle_id} className="border-t border-stone-100 hover:bg-stone-50/50" data-testid={`analytics-cycle-row-${c.cycle_id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      #{c.cycle_id?.slice(0, 8)}
                      {c.trigger === "auto_monthly" && <span className="ml-1 px-1 rounded bg-indigo-50 text-indigo-700 text-[9px]">AUTO</span>}
                    </td>
                    <td className="px-3 py-2 text-stone-500">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString("fr-FR") : "—"}
                    </td>
                    <td className="px-3 py-2 font-medium">{c.theme || <span className="text-stone-400">(en cours)</span>}</td>
                    <td className="px-3 py-2 text-stone-600">{c.region || "—"}</td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 text-[11px]">{c.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{(m.stripe?.revenue_eur || 0).toFixed(2)} €</td>
                    <td className="px-3 py-2 text-right font-mono">{m.newsletter?.signups ?? 0}</td>
                    <td className="px-3 py-2 text-right font-mono">{m.resend?.transactional_sent ?? 0}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {(m.inbound?.contact_messages || 0) + (m.inbound?.producer_leads || 0) + (m.inbound?.ebook_leads || 0)}
                    </td>
                  </tr>
                );
              })}
              {cycles.length === 0 && !loading && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-stone-500">Aucun cycle enregistré pour le moment.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminAnalyticsPage;
